import * as pc from "playcanvas";
import type { GameClient } from "../net/GameClient";
import { buildWeaponWorldModel } from "../player/weaponWorldModel";
import type { GroundItemsStateBroadcastMsg, GroundItemSnapshot } from "@hupper/shared/protocol";

// ── Airborne tumble + settle (visual only — the server still drives the
//    position arc). Mirrors the grenade's client-side tumble so a dropped
//    weapon flips end-over-end through the air and eases flat on landing
//    instead of the old "lay flat + spin around the vertical axis" look. ──
/** End-over-end tumble speed while airborne (deg/s). */
const TUMBLE_DPS = 560;
/** Tumble axis — mostly pitch (end-over-end) with a slight roll for life. */
const TUMBLE_AXIS = new pc.Vec3(1, 0, 0.16).normalize();
/** Seconds to ease from the tumbling pose into the flat ground rest pose. */
const SETTLE_DUR = 0.16;

// Reused scratch quats so the per-frame orientation math allocates nothing.
const _qFace = new pc.Quat();
const _qTumble = new pc.Quat();
const _qRest = new pc.Quat();
const _qOut = new pc.Quat();

/**
 * GroundItemRenderer — renders every server-authoritative dropped weapon
 * in the world. The server simulates gravity + floor settle and broadcasts
 * a snapshot each tick; we mirror that by spawning a PlayCanvas entity per
 * item (using the same procedural mesh family as the held view-model) and
 * lerping it toward the snapshot pose so airborne items read smoothly.
 *
 * Pickup is server-driven (the player presses E, server raycasts), so this
 * module is purely visual. The PickupPrompt UI module reads the same
 * snapshots to highlight the item under the crosshair.
 */
export class GroundItemRenderer {
  private readonly app: pc.AppBase;
  private readonly client: GameClient;
  private readonly unsubs: Array<() => void> = [];

  /**
   * Last known server snapshots indexed by id — exposed via `getItems()`
   * so the PickupPrompt can run its own client-side raycast against the
   * same data (server's pickup raycast is the source of truth, this is
   * only for UI hinting).
   */
  private snapshots = new Map<number, GroundItemSnapshot>();

  /** Live entities keyed by item id. */
  private entities = new Map<number, {
    entity: pc.Entity;       // holder — drives world position + orientation
    weaponId: string;
    /** Current lerp position (separate from authoritative snapshot). */
    x: number; y: number; z: number;
    /** Facing held constant through the airborne arc (the server spins yaw). */
    faceYaw: number;
    /** End-over-end tumble angle (deg) accumulated while airborne. */
    tumble: number;
    /** Settle ease progress: <0 = airborne, 0→1 = easing into rest pose. */
    settle: number;
    /** Holder orientation captured when the item rests, for the settle ease. */
    settleFromQuat: pc.Quat | null;
  }>();

  constructor(app: pc.AppBase, client: GameClient) {
    this.app = app;
    this.client = client;
    this.unsubs.push(this.client.onGroundItemsState((msg) => this.onState(msg)));
    // Lerp toward authoritative pose every frame.
    const onUpdate = (dt: number) => this.tickLerp(dt);
    this.app.on("update", onUpdate);
    this.unsubs.push(() => this.app.off("update", onUpdate));
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    for (const e of this.entities.values()) e.entity.destroy();
    this.entities.clear();
    this.snapshots.clear();
  }

  /** Read-only access to current snapshots (used by PickupPrompt). */
  getItems(): IterableIterator<GroundItemSnapshot> {
    return this.snapshots.values();
  }

  private onState(msg: GroundItemsStateBroadcastMsg): void {
    const live = new Set<number>();
    for (const s of msg.items) {
      live.add(s.id);
      this.snapshots.set(s.id, s);

      if (this.entities.has(s.id)) continue;
      // Wrap each iteration in try/catch so a builder regression on one
      // weaponId can't silently kill every subsequent item in the
      // snapshot (the symptom we hit before, where the for-loop bailed
      // mid-list and only the first drop rendered).
      try {
        // Holder drives world position + orientation; the model child holds
        // the rest-pose tilt. Splitting them lets the holder tumble the gun
        // while airborne and face a yaw at rest without touching the tilt.
        const holder = new pc.Entity(`ground-item-${s.id}`);
        const model = buildWeaponWorldModel(this.app, s.weaponId);
        // Rest pose — lay the weapon on its side, flat on the floor, the way
        // a dropped gun actually settles. The built model points its barrel
        // down local -Z with +Y up; a 90° roll about that barrel axis tips
        // it onto its side. In this orientation the gun's centre already
        // sits ~on the holder origin, so (unlike the old upright pose) no
        // extra position offset is needed — it rests where the server says,
        // and the holder can tumble it about its centre while airborne.
        model.setLocalEulerAngles(0, 0, 90);
        // Seat the thin side-lying gun on the floor: the server rests items
        // at a fixed height tuned for a taller pose, so drop the model a few
        // cm to kill the slight float (negligible during the airborne tumble).
        model.setLocalPosition(0, -0.05, 0);
        holder.addChild(model);
        this.app.root.addChild(holder);
        // Spawn at the server's reported pose (no first-frame lerp from
        // origin → would look like every item rocketed in from (0,0,0)).
        holder.setPosition(s.x, s.y, s.z);
        holder.setLocalEulerAngles(0, s.yaw, 0);
        this.entities.set(s.id, {
          entity: holder,
          weaponId: s.weaponId,
          x: s.x, y: s.y, z: s.z,
          faceYaw: s.yaw,
          tumble: 0,
          // Items already at rest when first seen (late-join) skip the tumble.
          settle: s.resting ? 1 : -1,
          settleFromQuat: null,
        });
      } catch (err) {
        console.warn(`[ground-item] failed to spawn entity for id=${s.id} weapon=${s.weaponId}:`, err);
      }
    }

    // Prune any entity whose item is no longer in the snapshot (picked up
    // server-side, round restart, etc.).
    for (const [id, entry] of this.entities) {
      if (live.has(id)) continue;
      entry.entity.destroy();
      this.entities.delete(id);
      this.snapshots.delete(id);
    }
  }

  /** Per-frame: lerp visual position toward the latest authoritative
   *  snapshot, and drive the orientation — an end-over-end tumble while
   *  airborne, easing into the flat ground pose once the server rests it. */
  private tickLerp(dt: number): void {
    // Higher = snappier; lower = smoother. Tuned so the toss-and-settle
    // animation reads naturally without lagging behind the server — high
    // enough that a wall ricochet's direction flip doesn't get corner-cut
    // into "bounced before touching the wall".
    const RATE = 26;
    const k = 1 - Math.exp(-RATE * dt);
    for (const [id, entry] of this.entities) {
      const s = this.snapshots.get(id);
      if (!s) continue;

      // ── Position ── always lerp toward the authoritative pose; once the
      // server flags rest AND the visual has caught up, pin it exactly.
      // (The old instant pin on `resting` yanked the item through the last
      // ~20 cm of lerp lag in a single frame — easing it in reads as the
      // weapon fluidly coming to rest instead of snapping to the floor.)
      entry.x += (s.x - entry.x) * k;
      entry.y += (s.y - entry.y) * k;
      entry.z += (s.z - entry.z) * k;
      if (s.resting) {
        const err = Math.hypot(s.x - entry.x, s.y - entry.y, s.z - entry.z);
        if (err < 0.01) { entry.x = s.x; entry.y = s.y; entry.z = s.z; }
      }
      entry.entity.setPosition(entry.x, entry.y, entry.z);

      // ── Orientation ──
      if (!s.resting) {
        // Airborne: tumble end-over-end about a horizontal axis, facing held
        // constant (ignore the server's decorative yaw spin). The holder is
        // centred on the gun, so rotating it tumbles the weapon in place.
        entry.settle = -1;
        entry.settleFromQuat = null;
        entry.tumble = (entry.tumble + TUMBLE_DPS * dt) % 360;
        _qFace.setFromEulerAngles(0, entry.faceYaw, 0);
        _qTumble.setFromAxisAngle(TUMBLE_AXIS, entry.tumble);
        _qOut.mul2(_qFace, _qTumble);
        entry.entity.setLocalRotation(_qOut);
      } else if (entry.settle < 1) {
        // Just landed: ease from the last tumble pose to the flat rest pose
        // at the server's final (15°-snapped) yaw so it doesn't snap.
        if (entry.settleFromQuat == null) {
          entry.settleFromQuat = entry.entity.getLocalRotation().clone();
          entry.settle = 0;
        }
        entry.settle = Math.min(1, entry.settle + dt / SETTLE_DUR);
        const t = 1 - Math.pow(1 - entry.settle, 3); // ease-out cubic
        _qRest.setFromEulerAngles(0, s.yaw, 0);
        _qOut.slerp(entry.settleFromQuat, _qRest, t);
        entry.entity.setLocalRotation(_qOut);
      } else {
        // Fully settled — pin the flat rest pose.
        entry.entity.setLocalEulerAngles(0, s.yaw, 0);
      }
    }
  }
}
